import {useEffect, useRef, useState} from 'react';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import {api, errorMessage, jsonInit} from '../api/client';
import Spinner from '../components/feedback/Spinner';
import {useAuth} from '../hooks/useAuth';
import {AuthTokens} from '../types';

// Where the emailed link lands: redeems the one-time token, stores the session, moves on.
export default function PasswordlessLoginPage() {
    const [params] = useSearchParams();
    const {onAuthenticated} = useAuth();
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);
    const started = useRef(false);

    useEffect(() => {
        if (started.current) return; // StrictMode double-invokes effects; the token is one-time
        started.current = true;
        const token = params.get('token');
        if (!token) {
            setError('That link is missing its token.');
            return;
        }
        api<AuthTokens>('/api/auth/authenticate-with-token', jsonInit('POST', {token}))
            .then(tokens => {
                onAuthenticated(tokens);
                const redir = params.get('redir');
                // /authorize is served by the Worker (an assistant's OAuth consent page), not
                // the SPA, so it needs a real navigation rather than a route change.
                if (redir?.startsWith('/authorize')) {
                    window.location.replace(redir);
                    return;
                }
                navigate(redir && redir.startsWith('/') ? redir : '/journal', {replace: true});
            })
            .catch(e => setError(errorMessage(e)));
    }, [params, onAuthenticated, navigate]);

    if (error) {
        return (
            <div className="settle-in flex flex-col gap-4 pt-10">
                <h1 className="title m-0">That link didn&rsquo;t work</h1>
                <p className="problem m-0">{error}</p>
                <Link to="/signin" className="btn btn-primary">
                    Ask for a new link
                </Link>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center gap-3 pt-24 text-ink-2">
            <Spinner label="Signing you in" />
            Signing you in…
        </div>
    );
}
